<template>
  <div>
    <div v-if="!loading">
      <div class="font-weight-bold pa-0 pt-2">
        <span v-if="buySell === `sell`">Ship To Address</span>
        <span v-else>Shipping Address</span>
      </div>
      <v-autocomplete
        dense
        required
        v-model="selectedShippingAddress"
        :items="getItems()"
        :hint="getAddressHint()"
        :persistent-hint="true"
        item-text="title"
        item-value="id"
        :label="(buySell === `sell`) ? 'Ship To Address' : 'Shipping Address'"
        single-line
        return-object
        :rules="[validateShipping, addressValidator]"
        :append-outer-icon="buySell === 'buy' ? 'mdi-plus' : null"
        @click:append-outer="addNewAddress('shipping')"
      >
        <template v-if="buySell === `sell`" v-slot:selection="data">
          {{ data.item.label }}
        </template>
        <template v-else v-slot:selection="data">
          {{ data.item.location.label }} -
          {{ data.item.location.addressee }}
        </template>
        <template v-if="buySell === `sell`" v-slot:item="data">
          {{ data.item.label }}
        </template>
        <template v-else v-slot:item="data">
          {{ data.item.location.label }} -
          {{ data.item.location.addressee }}
        </template>
      </v-autocomplete
      >
    </div>
    <v-progress-linear
      class="mt-4 mb-4"
      indeterminate
      v-else
    ></v-progress-linear>
  </div>
</template>

<script>
import orderMixin from '@/mixins/order'
import {mapGetters} from 'vuex'
import {FulfillmentTypes} from '@/util/constants'
import {get} from 'lodash'

/**
 * Shipping Address Component
 * On mount: set default shipping address for SO, default warehouse location for PO
 */

export default {
  mixins: [orderMixin],
  props: {
    buySell: String,
    companyId: [String, Number],
    addressValidator: {
      type: Function,
      default() {
        return () => true
      },
    },
  },
  data() {
    return {
      loading: true,
    }
  },
  watch: {
    companyId: {
      handler() {
        this.init()
      },
    },
  },
  mounted() {
    this.init()
  },
  computed: {
    ...mapGetters([
      'getDefaultShippingAddress',
      'getShippingInstructions',
      'getWarehouseLocations',
    ]),
    fulfillmentType: {
      get() {
        return (
          this.$store.getters['getCartField'](
            `${this.buySell}.fulfillmentType`
          ) || FulfillmentTypes.STANDARD
        )
      },
    },
    selectedShippingInstructions: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedShippingInstructions`
        )
      },
    },
    selectedShippingAddress: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.selectedShippingAddress`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.selectedShippingAddress`,
          value,
        })
      },
    },
  },
  methods: {
    async init() {
      this.loading = true
      if (!get(this, `getDefaultShippingAddress.id`))
        await this.$store.dispatch('refreshSelectedCompany')
      const addressPromise =
        this.buySell === `buy`
          ? this.$store.dispatch('fetchSelectedCompanyAddresses', {
            page: 1,
            itemsPerPage: 10000,
          })
          : this.$store.dispatch('fetchWarehouseLocations')
      await addressPromise
      if (
        !get(this, `selectedShippingAddress.id`) &&
        this.fulfillmentType === FulfillmentTypes.STANDARD
      ) {
        if (this.buySell === `buy`) {
          this.selectedShippingAddress = this.getDefaultShippingAddress
        } else {
          // Warehouse locations are prioritized, so use the highest priority.
          this.selectedShippingAddress = this.getWarehouseLocations[0]
        }
      }
      // Make sure the selectedShippingAddress is in getItems(), otherwise unset it.
      if (
        this.selectedShippingAddress &&
        !this.getItems().find(
          (item) => item.id === this.selectedShippingAddress.id
        )
      ) {
        this.selectedShippingAddress = null
      }
      this.$emit('loaded')
      this.loading = false
    },
    validateShipping(v) {
      return !!get(v,'id',false) || get(v,'tbd',false) || 'Shipping address is required.'
    },
    getAddressHint() {
      return this.buySell === `buy`
        ? this.addressHint(this.selectedShippingAddress)
        : this.warehouseAddressHint(this.selectedShippingAddress)
    },
    getItems() {
      return this.buySell === `buy`
        ? this.getAvailableShippingAddresses(this.selectedShippingInstructions)
        : this.getWarehouseLocations
    },
    addNewAddress(type) {
      this.$emit('addNewAddress', {type})
    },
  },
}
</script>
